import { AlertTriangle, Sparkles, Star, Heart, RefreshCw, WifiOff } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton Loaders
// ─────────────────────────────────────────────────────────────────────────────

function SkeletonBase({ className }: { className?: string }) {
  return (
    <div className={cn('shimmer rounded-md', className)} aria-hidden="true" />
  )
}

export function SkeletonText({ lines = 1, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-label="Loading…">
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBase
          key={i}
          className={cn('h-4', i === lines - 1 && lines > 1 ? 'w-3/5' : 'w-full')}
        />
      ))}
    </div>
  )
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn('bg-cosmic-nebula/50 ring-1 ring-white/5 rounded-2xl p-5 flex flex-col gap-3', className)}
      aria-label="Loading…"
    >
      <SkeletonBase className="h-4 w-2/5" />
      <SkeletonBase className="h-3 w-4/5" />
      <SkeletonBase className="h-3 w-3/5" />
      <div className="flex gap-2 mt-1">
        <SkeletonBase className="h-6 w-16 rounded-full" />
        <SkeletonBase className="h-6 w-12 rounded-full" />
      </div>
    </div>
  )
}

export function SkeletonTimeline({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3', className)} aria-label="Loading…">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <SkeletonBase className="h-10 w-10 rounded-xl flex-none" />
          <div className="flex flex-col gap-1.5 flex-1">
            <SkeletonBase className="h-3.5 w-1/3" />
            <SkeletonBase className="h-3 w-1/2" />
          </div>
          <SkeletonBase className="h-6 w-20 rounded-full flex-none" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonChart({ className }: { className?: string }) {
  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      aria-label="Loading chart…"
    >
      <div className="flex items-end gap-1.5 h-32">
        {[40, 65, 45, 80, 55, 70, 50, 75, 60, 85, 50, 65].map((h, i) => (
          <div
            key={i}
            className="shimmer flex-1 rounded-sm"
            style={{ height: `${h}%` }}
            aria-hidden="true"
          />
        ))}
      </div>
      <SkeletonBase className="h-3 w-full" />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Spinner
// ─────────────────────────────────────────────────────────────────────────────

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg'

const spinnerSizes: Record<SpinnerSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
}

/**
 * Animated spinner for inline loading states (button loading, inline fetches).
 * For full-section loading use the Skeleton* components instead.
 */
export function Spinner({
  size = 'md',
  className,
  label = 'Loading…',
}: {
  size?: SpinnerSize
  className?: string
  /** Screen-reader label (default "Loading…") */
  label?: string
}) {
  return (
    <span
      role="status"
      className={cn('inline-flex items-center justify-center', className)}
    >
      <svg
        className={cn('animate-spin text-gold', spinnerSizes[size])}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-20"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  )
}

/** Centered full-section spinner with optional label text. */
export function SpinnerPage({
  label = 'Loading…',
  className,
}: {
  label?: string
  className?: string
}) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-3 py-16', className)}
      role="status"
    >
      <Spinner size="lg" label={label} />
      <p className="text-star-dim text-sm">{label}</p>
    </div>
  )
}

/** @deprecated Use SkeletonCard, SkeletonText, etc. instead. Kept for incremental migration. */
export function LoadingSpinner() {
  return <SkeletonCard className="max-w-sm mx-auto" />
}

/** Full-page dashboard skeleton: gauge + 2 panels side by side */
export function SkeletonDashboard({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-6', className)} aria-label="Loading dashboard…">
      {/* Gauge row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-cosmic-nebula/50 ring-1 ring-white/5 rounded-2xl p-5 flex flex-col items-center gap-3">
          <SkeletonBase className="w-32 h-32 rounded-full" />
          <SkeletonBase className="h-3 w-20" />
        </div>
        <div className="md:col-span-2 bg-cosmic-nebula/50 ring-1 ring-white/5 rounded-2xl p-5 space-y-3">
          <SkeletonBase className="h-4 w-1/3" />
          <SkeletonTimeline rows={4} />
        </div>
      </div>
      {/* Bottom row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  )
}

/** Birth chart page skeleton: north-indian grid + interpretation panel */
export function SkeletonBirthChart({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-6', className)} aria-label="Loading birth chart…">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart grid placeholder */}
        <div className="bg-cosmic-nebula/50 ring-1 ring-white/5 rounded-2xl p-5">
          <SkeletonBase className="h-4 w-1/3 mb-4" />
          <div className="grid grid-cols-4 grid-rows-4 gap-1 aspect-square">
            {Array.from({ length: 16 }).map((_, i) => (
              <SkeletonBase key={i} className="rounded-sm" />
            ))}
          </div>
        </div>
        {/* Planetary table */}
        <div className="bg-cosmic-nebula/50 ring-1 ring-white/5 rounded-2xl p-5 space-y-3">
          <SkeletonBase className="h-4 w-2/5 mb-4" />
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <SkeletonBase className="w-7 h-7 rounded-lg flex-none" />
              <SkeletonBase className="h-3 w-1/3" />
              <SkeletonBase className="h-3 w-1/4 ml-auto" />
            </div>
          ))}
        </div>
      </div>
      <SkeletonCard />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Error State
// ─────────────────────────────────────────────────────────────────────────────

type ErrorVariant = 'default' | 'network' | 'not-found' | 'forbidden'

const errorConfig: Record<ErrorVariant, { icon: typeof AlertTriangle; defaultTitle: string }> = {
  default:    { icon: AlertTriangle, defaultTitle: 'Something went wrong' },
  network:    { icon: WifiOff,       defaultTitle: 'Connection error' },
  'not-found':{ icon: Sparkles,      defaultTitle: 'Not found' },
  forbidden:  { icon: AlertTriangle, defaultTitle: 'Access denied' },
}

export function ErrorState({
  message,
  onRetry,
  title,
  variant = 'default',
  compact = false,
}: {
  message: string
  onRetry?: () => void
  title?: string
  variant?: ErrorVariant
  /** Render a smaller inline version without extra vertical padding */
  compact?: boolean
}) {
  const { icon: Icon, defaultTitle } = errorConfig[variant]

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 text-center',
        compact ? 'py-8' : 'py-16',
      )}
      role="alert"
    >
      <div className="w-12 h-12 rounded-2xl bg-inauspicious/10 border border-inauspicious/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-inauspicious/70" aria-hidden="true" />
      </div>
      <div className="space-y-1 max-w-xs">
        <p className="text-star-bright text-sm font-medium">{title ?? defaultTitle}</p>
        <p className="text-star-muted text-xs leading-relaxed">{message}</p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg
                     bg-cosmic-dusk border border-cosmic-mist text-star-soft text-sm
                     hover:bg-cosmic-aurora hover:text-star-bright hover:border-violet-dark
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50
                     active:scale-[0.98] transition-all duration-150"
        >
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
          Try again
        </button>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty State
// ─────────────────────────────────────────────────────────────────────────────

export function EmptyState({
  title = 'Nothing here yet',
  message,
  ctaLabel,
  ctaHref,
  onCtaClick,
  icon,
  compact = false,
}: {
  title?: string
  message?: string
  ctaLabel?: string
  /** Link destination — use for navigation CTAs */
  ctaHref?: string
  /** Click handler — use for action CTAs (mutually exclusive with ctaHref) */
  onCtaClick?: () => void
  icon?: React.ReactNode
  /** Render a smaller version without extra vertical padding */
  compact?: boolean
}) {
  const ctaClass =
    'px-5 py-2 rounded-lg bg-gold/10 border border-gold/20 text-gold text-sm font-medium ' +
    'hover:bg-gold/20 hover:border-gold/30 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 ' +
    'active:scale-[0.98] transition-all duration-150'

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-5 text-center',
        compact ? 'py-8' : 'py-16',
      )}
      role="status"
    >
      <div className="w-12 h-12 rounded-2xl bg-cosmic-dusk/50 ring-1 ring-white/5 flex items-center justify-center">
        {icon ?? <Sparkles className="w-5 h-5 text-gold/40" aria-hidden="true" />}
      </div>
      <div className="space-y-1 max-w-xs">
        <p className="text-star-bright text-sm font-medium">{title}</p>
        {message && <p className="text-star-muted text-xs leading-relaxed">{message}</p>}
      </div>
      {ctaLabel && ctaHref && (
        <Link to={ctaHref} className={ctaClass}>
          {ctaLabel}
        </Link>
      )}
      {ctaLabel && onCtaClick && !ctaHref && (
        <button onClick={onCtaClick} className={ctaClass}>
          {ctaLabel}
        </button>
      )}
    </div>
  )
}

/** @deprecated Use EmptyState instead. Kept for incremental migration. */
export function SetupPrompt({ message }: { message?: string }) {
  return (
    <EmptyState
      title="Birth details not set up"
      message={message ?? 'Complete your birth details to see personalised insights.'}
      ctaLabel="Set up birth details"
      ctaHref="/onboarding"
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Domain-specific Empty States
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shown on BirthChartPage when the user hasn't entered birth details yet.
 * Guides them through the onboarding flow with cosmic imagery.
 */
export function NoBirthChartEmpty() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-6 text-center" role="status">
      {/* Decorative north-indian chart placeholder */}
      <div className="relative w-24 h-24" aria-hidden="true">
        <div className="absolute inset-0 rounded-2xl bg-cosmic-dusk/70 ring-1 ring-white/5" />
        <div className="absolute inset-3 rounded-xl bg-cosmic-nebula/60 ring-1 ring-white/5" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Star className="w-8 h-8 text-gold/40" />
        </div>
      </div>

      <div className="max-w-xs space-y-2">
        <p className="text-star-bright font-semibold text-base">Your birth chart awaits</p>
        <p className="text-star-dim text-sm leading-relaxed">
          Enter your birth details to generate your personalised Vedic birth chart with
          planetary positions, houses, and Nakshatra analysis.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Link
          to="/onboarding"
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet to-violet-mid
                     text-star-white font-medium text-sm
                     hover:shadow-glow-violet transition-all active:scale-[0.98]
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
        >
          Set up my birth chart
        </Link>
        <Link
          to="/settings"
          className="text-sm text-star-dim hover:text-star-soft transition-colors"
        >
          Already set up? Check settings →
        </Link>
      </div>
    </div>
  )
}

/**
 * Shown on CompatibilityPage when the user hasn't added a partner's details.
 */
export function NoCompatibilityEmpty() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-6 text-center" role="status">
      <div className="flex items-center gap-3" aria-hidden="true">
        <div className="w-14 h-14 rounded-2xl bg-cosmic-dusk/70 ring-1 ring-white/5 flex items-center justify-center">
          <Star className="w-6 h-6 text-gold/40" />
        </div>
        <Heart className="w-5 h-5 text-inauspicious/40" />
        <div className="w-14 h-14 rounded-2xl bg-cosmic-dusk/50 ring-1 ring-white/5 ring-dashed flex items-center justify-center">
          <span className="text-star-dim text-xl">?</span>
        </div>
      </div>

      <div className="max-w-xs space-y-2">
        <p className="text-star-bright font-semibold text-base">Add a partner to compare charts</p>
        <p className="text-star-dim text-sm leading-relaxed">
          Enter your partner's birth details to calculate Ashtakoot compatibility,
          Guna Milan score, and Mangal Dosha analysis.
        </p>
      </div>

      <Link
        to="/settings"
        className="px-6 py-2.5 rounded-xl bg-gold/10 border border-gold/20
                   text-gold font-medium text-sm
                   hover:bg-gold/20 hover:border-gold/30 transition-all active:scale-[0.98]
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50"
      >
        Add partner details
      </Link>
    </div>
  )
}
